import { ref } from "vue";
import type { Router } from "vue-router";
import type { App } from "vue";

const CHUNK_ERROR_PATTERNS = [
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /Unable to preload CSS/i,
    /Loading CSS chunk/i,
    /Loading chunk/i,
];

const RELOAD_FLAG_KEY = "app:chunk-reload-attempted";

function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export const showUpdatePrompt = ref(false);

export function registerChunkErrorHandling(router: Router, app: App): void {
    router.onError((error, to) => {
        if (!isChunkLoadError(error)) {
            console.error(error); // or Sentry.captureException(error)
            return;
        }

        if (sessionStorage.getItem(RELOAD_FLAG_KEY) === "1") {
            // Already tried a hard reload this session and it recurred —
            // don't loop, hand off to a manual "refresh" prompt instead.
            showUpdatePrompt.value = true;
            return;
        }

        sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
        const target = to ? router.resolve(to).href : window.location.href;
        window.location.assign(target);
    });

    // Backstop: render-time throws that happen *after* a stale module has
    // already loaded, so they never pass through router.onError.
    const previousErrorHandler = app.config.errorHandler;
    app.config.errorHandler = (error, instance, info) => {
        if (isChunkLoadError(error)) {
            // Preserve existing error handling (e.g. Sentry) before reload/prompt.
            previousErrorHandler?.(error, instance, info);

            if (sessionStorage.getItem(RELOAD_FLAG_KEY) !== "1") {
                sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
                window.location.reload();
                return;
            }
            showUpdatePrompt.value = true;
            return;
        }

        if (previousErrorHandler) {
            previousErrorHandler(error, instance, info);
            return;
        }

        console.error(error, info);
    };
}
